// A session's ledger, folded for reading (F-SES-21 rule 6, spec/13 §3.3). A pass or free time is charged a minute
// at a time, so a five-hour session posts hundreds of identical "Charge" entries; consecutive identical charges fold
// into one run ("Charge · Pass · 0:01 each × 27") that a surface can open to the entries it holds. Every other entry
// (free time added, a reversal, a promotion) stays a run of one, and a run never spans one, so the order is the
// ledger's. The entries come newest first, as `…/sessions/{s}/ledger` pages them.
//
// No I/O, and no runtime import beyond this file: the console runs it in the browser under a policy that forbids
// compiling code from strings, which the contracts' schemas would do.
/** The entry kinds that fold when they repeat: the per-minute and per-unit play charges. */
const FOLDING_KINDS = new Set(['segment_charge']);
/** Two entries fold into one run when they are the same charge, from the same source, for the same time and money. */
function sameCharge(a, b) {
    return (FOLDING_KINDS.has(a.kind) &&
        !a.reversal &&
        a.kind === b.kind &&
        a.reversal === b.reversal &&
        a.memo === b.memo &&
        a.funding === b.funding &&
        a.seconds === b.seconds &&
        a.amount.amount === b.amount.amount &&
        a.amount.currency === b.amount.currency);
}
/**
 * Fold the entries (newest first, as the pages concatenate) into runs. An entry that appears twice (a page re-read
 * while the session posted) is kept once.
 */
export function ledgerRuns(entries) {
    const seen = new Set();
    const runs = [];
    for (const entry of entries) {
        if (seen.has(entry.id))
            continue;
        seen.add(entry.id);
        const run = runs.at(-1);
        const previous = run?.at(-1);
        if (run !== undefined && previous !== undefined && sameCharge(previous, entry))
            run.push(entry);
        else
            runs.push([entry]);
    }
    return runs.map((run) => ({ key: run.at(-1).id, entries: run }));
}
/**
 * What a run adds up to: its play seconds and its money, in integer minor units of the entries' one currency (a
 * session has one). Summed as `bigint`, so no run of any length loses a paisa.
 */
export function ledgerRunTotals(run) {
    const first = run.entries[0];
    if (first === undefined)
        throw new RangeError('A ledger run holds at least one entry.');
    let seconds = 0;
    let minor = 0n;
    for (const entry of run.entries) {
        if (entry.amount.currency !== first.amount.currency)
            throw new RangeError('A ledger run is in one currency.');
        seconds += entry.seconds;
        minor += BigInt(entry.amount.amount);
    }
    return { seconds, amount: { amount: Number(minor), currency: first.amount.currency } };
}
//# sourceMappingURL=ledger-runs.js.map